import { Component, h, Ref, useContext, useEffect, useMemo, useRef, useState, WriteOnlyRef } from "@lukekaalim/act";
import { useBeat, Particle, Simulation, Animation, bezier4, getAnimationPoint, createJanitor, subscribeEvent, KeyboardInput, useLogger } from "@kosmod/kit"
import { Graphics3DSetup, graphicsSetupContext, inputBeat, renderBeat } from "@kosmod/graphics-3d"
import { a3, PropsFromClass } from "@lukekaalim/act-three";
import * as three from 'three';

export type DrivableCameraProps = {
  ref?: WriteOnlyRef<DrivableCameraInstance>,

  autoAssignToGraphics?: boolean,
  keyboard: null | KeyboardInput,

  graphics?: Graphics3DSetup,
} & Omit<PropsFromClass<three.PerspectiveCamera>, 'ref' | 'position' | 'rotation'>

/**
 * The internal mutable values of the drivable camera
 */
export type DrivableCameraInstance = {
  targetPosition: three.Vector3,
  targetOffset: three.Vector2,
  rotation: number,
  vertical: number,

  forward: three.Vector2,

  object: null | three.PerspectiveCamera,
}

export const DrivableCamera: Component<DrivableCameraProps> = ({
  children,
  autoAssignToGraphics = true,
  keyboard,
  ref,

  graphics: externalGraphics,
  ...props
}) => {
  const contextGraphics = useContext(graphicsSetupContext);
  const graphics = externalGraphics || contextGraphics;

  const logger = useLogger('DrivableCamera');

  const [instance] = useState<DrivableCameraInstance>({
    targetPosition: new three.Vector3(),
    targetOffset: new three.Vector2(),
    rotation: 0.25,
    vertical: 5,
    forward: new three.Vector2(),
    object: null,
  });

  useMemo(() => {
    if (ref) {
      ref.set(instance)
    }
  }, [ref])

  const simData = useRef(() => {
    const sim = new Simulation();
    const cameraParticle = new Particle()
    sim.particles.push(cameraParticle);

    return {
      sim,
      cameraParticle,
      rotation: {
        target: 0.25,
        anim: Animation(
          0, 0,
          [0.25, 0.25, 0.25, 0.25],
          (pts, t) => bezier4(pts[0], pts[1], pts[2], pts[3], t)
        ),
      },
      vertical: {
        target: 5,
        anim: Animation(
          0, 0,
          [5, 5, 5, 5],
          (pts, t) => bezier4(pts[0], pts[1], pts[2], pts[3], t)
        ),
      },
    }
  }).get();

  useEffect(() => {
    const camera = graphics.camera.get();
    const canvas = graphics.canvas.get();
    if (!canvas || !camera || !keyboard)
      return;

    const j = createJanitor();

    const { sim, cameraParticle, rotation, vertical } = simData;

    let gamepad: Gamepad | null = window.navigator.getGamepads()[0];

    const onGamepadConnect = (event: GamepadEvent) => {
      gamepad = event.gamepad;
      logger.log({gamepad})
    };
    const onGamepadDisconnect = (event: GamepadEvent) => {
      gamepad = null;
      logger.log({gamepad})
    };

    window.addEventListener('gamepadconnected', onGamepadConnect);
    window.addEventListener('gamepaddisconnected', onGamepadDisconnect);
    j.add(() => {
        window.removeEventListener('gamepadconnected', onGamepadConnect);
        window.removeEventListener('gamepaddisconnected', onGamepadDisconnect);
    })

    const updateRotationAnimation = () => {
      const rotationPoint = getAnimationPoint(rotation.anim, performance.now())

      rotation.anim.span.start = performance.now()
      rotation.anim.span.length = 500;
      rotation.anim.values = [rotationPoint, rotation.target , rotation.target , rotation.target ]
    }

    j.add(keyboard.onKeyPress.subscribe(function onKeyDown(event: KeyboardEvent) {
      if (event.repeat)
        return;
      switch (event.code) {
        case 'KeyQ':
          rotation.target -= 0.25;
          break;
        case 'KeyE':
          rotation.target += 0.25;
          break;
        case 'KeyR':
          vertical.target -= 0.5;
          break;
        case 'KeyF':
          vertical.target += 0.5;
          break;
      }
      const currentZoomPoint = getAnimationPoint(vertical.anim, performance.now());
      updateRotationAnimation();

      vertical.anim.span.start = performance.now()
      vertical.anim.span.length = 250;
      vertical.anim.values = [currentZoomPoint, vertical.target , vertical.target , vertical.target ]
    }));

    const getAxisDirection = (negativeKey: string, positiveKey: string) => {
      if (keyboard.heldKeyCodes.has(negativeKey)) {
        if (keyboard.heldKeyCodes.has(positiveKey))
          return 0;
        return -1;
      }
      if (keyboard.heldKeyCodes.has(positiveKey))
        return 1;
      return 0;
    }
    
    // Forward and right vectors are computed at Input time
    const right = new three.Vector2(0, 0);

    let rotateRightPressed = false;
    let rotateLeftPressed = false;
    
    j.add(subscribeInput((now, _, frame) => {

      const rotationPoint = getAnimationPoint(rotation.anim, now)
      const verticalPoint = getAnimationPoint(vertical.anim, now);

      instance.forward.set(
        Math.sin((rotationPoint * Math.PI)),
        Math.cos((rotationPoint * Math.PI))
      )
      right.set(
        Math.sin((rotationPoint * Math.PI) + (0.5 * Math.PI)),
        Math.cos((rotationPoint * Math.PI) + (0.5 * Math.PI))
      )

      if (gamepad) {
        const gamepadX = gamepad.axes[0];
        const gamepadY = gamepad.axes[1];
        
        cameraParticle.acceleration.x = (gamepadX * right.x) + (gamepadY * instance.forward.x);
        cameraParticle.acceleration.y = (gamepadX * right.y) + (gamepadY * instance.forward.y);

        if (cameraParticle.acceleration.length() < 0.1)
          cameraParticle.acceleration.set(0, 0)

        if (gamepad.buttons[4].pressed && !rotateLeftPressed) {
          rotateLeftPressed = true;
          rotation.target -= 0.25;
          updateRotationAnimation();
        } else if (!gamepad.buttons[4].pressed && rotateLeftPressed) {
          rotateLeftPressed = false;
        }

        if (gamepad.buttons[5].pressed && !rotateRightPressed) {
          rotateRightPressed = true;
          rotation.target += 0.25;
          updateRotationAnimation();
        } else if (!gamepad.buttons[5].pressed && rotateRightPressed) {
          rotateRightPressed = false;
        }
      } else {
        const keyboardX = getAxisDirection('KeyA', 'KeyD')
        const keyboardY = getAxisDirection('KeyW', 'KeyS')
        cameraParticle.acceleration.x = (keyboardX * right.x * 2) + (keyboardY * instance.forward.x * 2);
        cameraParticle.acceleration.y = (keyboardX * right.y * 2) + (keyboardY * instance.forward.y * 2);
      }

      cameraParticle.velocity.clampLength(0, 10);
      
      sim.tick();

      instance.targetOffset.x = instance.forward.x * 5
      instance.targetOffset.y = instance.forward.y * 5
      instance.vertical = verticalPoint;
      instance.rotation = rotationPoint;
      instance.targetPosition.set(cameraParticle.position.x, 5, cameraParticle.position.y).divideScalar(100)


      camera.position.x = instance.targetOffset.x + (cameraParticle.position.x / 100);
      camera.position.y = instance.vertical;
      camera.position.z = instance.targetOffset.y + (cameraParticle.position.y / 100);

      camera.lookAt(new three.Vector3(camera.position.x - instance.targetOffset.x, 0, camera.position.z - instance.targetOffset.y))
      camera.updateWorldMatrix(false, false);
    }))
    
    return j.cleanup;
  }, [keyboard]);

  const subscribeRender = useBeat(renderBeat)
  const subscribeInput = useBeat(inputBeat)

  return h(a3.perspectiveCamera, {
    ...props,
    //rotation: new three.Euler(Math.PI * -0.25, 0, 0),
    ref: {
      set(value) {
        if (autoAssignToGraphics)
          graphics.camera.set(value);
        instance.object = value;
      },
    }
  }, children);
};